import React, { useEffect, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';

import Button from '@prisjakt/ui-button';
import { Card, CardMedia } from '@prisjakt/ui/card';
import Clickable from '@prisjakt/ui-clickable';
import { Column } from '@prisjakt/ui/list/variants';
import { TitleMediumText } from '@prisjakt/ui/text/variants';
import ProsAndCons from '../../../product-test/components/pros-and-cons';
import Text from '../../../components/text';
import ExpertUserCard from '../../../components/expert-user-card';
import Badge from '../../../components/badge';

import * as S from './styles';

import {
  trackExpertToplistSummaryViewed,
  trackExpertToplistSummaryClicked,
  trackExpertToplistSummaryTextExpanded,
} from '../../../tracking';

import { Product, AdvizedToplist } from '../../../../../types';
import { ContentTypes } from '../../../types';

type Props = {
  product: Product;
  toplist: AdvizedToplist;
};

function ToplistSummary({ product, toplist }: Props) {
  const { t } = useContext(AppContext);

  useEffect(() => {
    trackExpertToplistSummaryViewed({ title: toplist.title });
  }, []);

  if (!toplist) return null;

  const { products } = toplist;
  const selectedProduct = products.find(
    p => p.id.toString() === product.id.toString()
  );

  if (!selectedProduct) return null;

  return (
    <Card>
      {product.media.first && (
        <CardMedia>
          <S.PositionInList position={selectedProduct.order} />
          <S.ProductImage src={product.media.first} />
        </CardMedia>
      )}

      <S.Content>
        <TitleMediumText>
          <Badge type={ContentTypes.TOPLIST} />: {toplist.title}
        </TitleMediumText>

        {selectedProduct && (
          <>
            <Column gap={0.5}>
              <ProsAndCons type="pros" strings={[selectedProduct.plus]} />
              <ProsAndCons type="cons" strings={[selectedProduct.minus]} />
            </Column>

            {selectedProduct.description && (
              <div>
                <Text
                  lineCount={8}
                  text={selectedProduct.description}
                  onExpand={() => {
                    trackExpertToplistSummaryTextExpanded({
                      title: toplist.title,
                    });
                  }}
                />
              </div>
            )}
          </>
        )}

        <ExpertUserCard
          experts={[toplist.expert]}
          subtitle={t('expert', 'expert.role')}
        />

        <Button
          component={<Clickable to={toplist.url} />}
          style={{ width: '100%' }}
          color="primary"
          variant="outlined"
          onClick={() =>
            trackExpertToplistSummaryClicked({ title: toplist.title })
          }
        >
          Se hela topplistan
        </Button>
      </S.Content>
    </Card>
  );
}

export default ToplistSummary;
